import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { getCompany, postCompany, putCompany } from "store/thunks";
import * as Yup from "yup";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin, Switch } from "antd";
import NaverMap from "components/Common/NaverMap";
import SearchAddress from "components/Common/SearchAddress";
import { useRole } from "components/Hooks/UserHooks";
import { ROLES_FOR_APP, isHavePermissionRole } from "helpers/role";
import UploadFile from "components/Common/UploadFile";
import DropdownAsyncField from "components/Common/DropdownAsyncField";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import { LIST_OPTION_SNS_TYPE } from "helpers/constans";
import i18n from "i18n";
import { formatAddressByNaver } from "helpers/format";
import addressApi from "api/addressApi";
 
export interface Props {
  id?: string;
  triggerRefresh?: () => void;
  onClose?: () => void;
}

registerLocale("en", en);
registerLocale("ko", ko);

const CompanyForm = ({ id = "", triggerRefresh, onClose }: Props) => {
  const { t } = useTranslation();
  const { userPermissions } = useRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const [isOpenSearchAddress, setIsOpenSearchAddress] =
    useState<boolean>(false);
  const [isLoadingMap, setLoadingMap] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const data = {
        // ...values,
        address: values?.address,
        contact_email: values?.contact_email,
        contact_name: values?.contact_name,
        contact_phone: values?.contact_phone,
        name: values?.name,
        is_active: values?.is_active,
        ...(values?.website_url?.trim() ? {website_url: values?.website_url?.trim()} : null),
        logo_url: values?.logo_url?.[0]?.filePath || '',
        // fields: values?.fields?.length > 0 ? values?.fields?.map((item: any) => (item?.value)) : null,
        socials: (!values?.socials?.[0]?.sns || !values?.socials?.[0]?.url) ? null : values?.socials,
        business_account: null
      }

      if(!id){
        data.business_account = values?.business_account
      }

      const response: any = id
        ? await putCompany(id, data)
        : await postCompany(data);
      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      // toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      toast(t('Invalid input'), CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    logo_url: Yup.array()
    .of(
      Yup.object({
        filePath: Yup.string().nullable(),
      }).required(`${t("This field is required")}`),
    ).required(`${t("This field is required")}`),
    name: Yup.string().trim().required(`${t("This field is required")}`),
    contact_phone: Yup.string().required(`${t("This field is required")}`)
      .matches(/^\d{1,11}$/, t("Phone number must be up to 11 digits")),
    contact_email: Yup.string().required(`${t("This field is required")}`)
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
        t("Invalid email address")
      ),
    contact_name: Yup.string().nullable(),
    is_active: Yup.boolean(),
    // fields: Yup.array()
    //   .of(Yup.mixed())
    //   .min(1, `${t("This field is required")}`)
    //   .required(`${t("This field is required")}`),
    website_url: Yup.string()
      .matches(
        /^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
        t("Invalid URL")
      )
      .nullable()
      .notRequired(),
    socials: Yup.array()
      .of(
        Yup.object().shape({
          // sns: Yup.string().test("sns", `${t("This field is required")}`, (value) => !!value),
          sns: Yup.string().nullable(),
          url: Yup.string().when("sns", {
            is: (value: any) => !!value,
            then: (rule) => {
              return rule.test(
                "sns",
                `${t("This field is required")}`,
                (target) => !!target?.trim()
              );
            },
          }),
        })
      )
      .nullable(),
    address: Yup.object().shape({
      address: Yup.string().required(`${t("This field is required")}`),
      short_address: Yup.string().nullable(),
      postal_code: Yup.string().nullable(),
      region1: Yup.string().nullable(),
      region2: Yup.string().nullable(),
      region3: Yup.string().nullable(),
      lat: Yup.number().nullable(),
      long: Yup.number().nullable(),
    }),
    business_account: Yup.object().shape({
      email: Yup.string()
        .email(t("Please enter a valid email address"))
        .notRequired()
        .test(
          "email-required-if-password",
          t("Email is required if password is filled"),
          function (email) {
            const password = this.parent.password;
            if (!!password?.trim() && !email?.trim() && !id) {
              return this.createError({ path: "business_account.email" });
            }
            return true;
          }
        ),
      password: Yup.string()
        .min(6, t("Password must be at least 6 characters"))
        .notRequired()
        .test(
          "password-required-if-email",
          t("Password is required if email is filled"),
          function (password) {
            const email = this.parent.email;
            if (!!email?.trim() && !password?.trim() && !id) {
              return this.createError({ path: "business_account.password" });
            }
            return true;
          }
        ),
    }),
  });

  const formik = useFormik({
    initialValues: {
      logo_url: null,
      name: "",
      // fields: null,
      website_url: "",
      socials: [
        {
          sns: "",
          url: ""
        }
      ],
      address: {
        address: "",
        short_address: "",
        postal_code: "",
        region1: "",
        region2: "",
        region3: "",
        lat: 0,
        long: 0,
      },
      contact_phone: "",
      contact_name: "",
      contact_email: "",
      business_account: {
        email: "",
        password: "",
      },
      is_active: true,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleCloseModalAddress = () => {
    setIsOpenSearchAddress(false);
  };

  const handleOpenSearchAddress = () => {
    setIsOpenSearchAddress(true);
  };

  const getLatLngByAddress = async (address: string) => {
    try {
      if (address) {
        const data: any = await addressApi.getGEOByAddress({
          query: encodeURIComponent(address),
        });
        let dataAddress = data?.data?.addresses;
        const formatAddress = formatAddressByNaver(dataAddress);

        if (formatAddress) {
          formik?.setFieldValue("address.address", formatAddress?.address || '');
          formik?.setFieldValue("address.short_address", formatAddress?.short_address || '');
          formik?.setFieldValue("address.postal_code", formatAddress?.postal_code || '');
          formik?.setFieldValue("address.region1", formatAddress?.region1 || '');
          formik?.setFieldValue("address.region2", formatAddress?.region2 || '');
          formik?.setFieldValue("address.region3", formatAddress?.region3 || '');
          formik?.setFieldValue("address.lat", formatAddress?.lat || 0);
          formik?.setFieldValue("address.long", formatAddress?.long || 0);
        }
      }
    } catch (error) {
      //
      console.log(error)
    }
  };

  const onSetValueAddress = async (data: any) => {
    if (data) {
      setLoadingMap(true);
      const res = await getLatLngByAddress(data);
      setLoadingMap(false);
    }
    setIsOpenSearchAddress(false);
  };

  const handleSetValueForm = (valueDefault: any) => {
    // const formatFields = valueDefault?.fields?.length > 0 ? (
    //   valueDefault?.fields?.map((item: any) => ({
    //     value: item?.id,
    //     label: (
    //       <div className="d-flex align-items-center gap-2">
    //         <div>
    //             {`${item?.title}`}
    //           </div>
    //       </div>
    //     ),
    //     data: item,
    //   }))
    // ) : null;

    formik.setValues({
      ...valueDefault,
      address: {
        ...valueDefault?.address,
        region1: valueDefault?.address?.region_1,
        region2: valueDefault?.address?.region_2,
        region3: valueDefault?.address?.region_3,
        postal_code: String(valueDefault?.address?.postal_code)
      },
      // fields: formatFields,
      logo_url: valueDefault?.logo_url ? [
        {
          fullPath: valueDefault?.logo_url
        }
      ] : [],
      socials: valueDefault?.socials?.[0]?.sns ? [
        {
          url: valueDefault?.socials?.[0]?.url || '',
          sns: valueDefault?.socials?.[0]?.sns || ''
        }
      ] : null,
      business_account: {
        email: valueDefault?.business_account?.email || '',
        password: valueDefault?.business_account?.password || '',
      }
    })
  };

  const handleCallAllOption = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      setIsLoadingDetail((_prev) => true);
      const resDetail: any = await getCompany(idItem);
      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  useEffect(() => {
    handleCallAllOption(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <React.Fragment>
      {isLoadingDetail && (
        <div
          style={{
            position: "absolute",
            zIndex: 3,
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgb(164 164 164 / 36%)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner size="sm" color="primary"></Spinner>
        </div>
      )}
      <div>
        <form onSubmit={formik.handleSubmit}>
          <div
            className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
            style={{
              border: "1px dashed var(--vz-primary)",
              borderRadius: "4px",
            }}
          >
            <span
              className="text-primary position-absolute fs-13 px-1"
              style={{
                backgroundColor: "var(--vz-secondary-bg)",
                top: "0px",
                transform: "translateY(-50%)",
              }}
            >
              {t("Info")}
            </span>
            <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
              <div>
                <div className="mb-3">
                  <Label htmlFor="logo" className="form-label">
                    {t("Logo")} <span className="text-danger">*</span>
                  </Label>
                  <UploadFile
                    formik={formik}
                    fieldValue="logo_url"
                    isMultiple={false}
                    isUploadMore={
                      !formik?.values?.logo_url ||
                      !(formik?.values?.logo_url as any)?.length
                    }
                  />
                  {formik.touched.logo_url && formik.errors.logo_url ? (
                    <div className="text-danger mt-2">{formik.errors.logo_url}</div>
                  ) : null}
                </div>
              </div>
              <div className="flex-grow-1">
                <Row>
                  <Col md={6} lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        {t("Name")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="name"
                        type="text"
                        className="form-control"
                        id="name"
                        placeholder={t("Name")}
                        value={formik?.values?.name}
                        onChange={(event: any) =>
                          formik.setFieldValue("name", event?.target?.value)
                        }
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.name}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  {/* <Col md={6} lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="fields" className="form-label">
                        {t("Fields")} <span className="text-danger">*</span>
                      </Label>
                      <DropdownAsyncField
                        name="fields"
                        placeholder={`${t("Fields")}...`}
                        isMulti={true}
                        isShowIconCheck={true}
                        hideSelectedOptions={false}
                        listCheckedOption={formik?.values?.fields}
                        onChangeSelect={(e: any) => {
                          formik.setFieldValue("fields", e)
                        }}
                        key={`category-${i18n?.language}`}
                      />
                      {formik.touched.fields && formik.errors.fields ? (
                        <div className="text-danger mt-2">
                          {formik.errors.fields}
                        </div>
                      ) : null}
                    </div>
                  </Col> */}
                  <Col md={6} lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="sns_type" className="form-label">
                        {t("SNS Type")}
                      </Label>
                      <DropdownOptionNew
                        name="sns_type"
                        dataList={LIST_OPTION_SNS_TYPE}
                        placeholder={`${t("SNS Type")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.socials?.[0]?.sns}
                        onChangeSelect={(e: any) => {
                          if(!e?.value) {
                            formik.setFieldValue("socials[0].url", "")
                          }
                          formik.setFieldValue("socials[0].sns", e?.value)
                        }}
                        isHasOptionAll={false}
                        lstImage={true}
                        isClearable={true}
                      />
                      {
                        // formik.touched.socials?.[0]?.sns &&
                        typeof formik.errors.socials?.[0] === "object" &&
                        formik.errors.socials?.[0]?.sns ? (
                          <div className="text-danger mt-2">
                            {formik.errors.socials[0].sns}
                          </div>
                        ) : null
                      }
                    </div>
                  </Col>
                  <Col md={6} lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="sns_link" className="form-label">
                        {t("SNS Link")}
                      </Label>
                      <Input
                        name="sns_link"
                        type="text"
                        className="form-control"
                        id="sns_link"
                        placeholder={t("SNS Link")}
                        value={formik?.values?.socials?.[0]?.url}
                        disabled={!formik?.values?.socials?.[0]?.sns}
                        onChange={(event: any) => {
                          if(!formik?.values?.socials?.[0]?.sns) {
                            formik.setFieldValue("socials[0].url", "")
                          }else {
                            formik.setFieldValue("socials[0].url", event?.target?.value)
                          }
                        }}
                      />
                      {
                        formik.touched.socials?.[0]?.url &&
                        typeof formik.errors.socials?.[0] === "object" &&
                        formik.errors.socials?.[0]?.url ? (
                          <div className="text-danger mt-2">
                            {formik.errors.socials[0].url}
                          </div>
                        ) : null
                      }
                    </div>
                  </Col>

                  <Col md={6} lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="website_url" className="form-label">
                        {t("Website")}
                      </Label>
                      <Input
                        name="website_url"
                        type="text"
                        className="form-control"
                        id="website_url"
                        placeholder={t("Website")}
                        value={formik?.values?.website_url}
                        onChange={(event: any) =>
                          formik.setFieldValue("website_url", event?.target?.value)
                        }
                      />
                      {formik.touched.website_url && formik.errors.website_url ? (
                        <div className="text-danger mt-2">
                          {formik.errors.website_url}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          <div
            className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
            style={{
              border: "1px dashed var(--vz-primary)",
              borderRadius: "4px",
            }}
          >
            <span
              className="text-primary position-absolute fs-13 px-1"
              style={{
                backgroundColor: "var(--vz-secondary-bg)",
                top: "0px",
                transform: "translateY(-50%)",
              }}
            >
              {t("Address")}
            </span>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <Label htmlFor="address" className="form-label">
                    {t("Address")}
                    <span className="text-danger"> *</span>
                  </Label>

                  <div className="d-flex mb-2 gap-2">
                    <Input
                      name="address"
                      type="text"
                      className="form-control"
                      id="address"
                      placeholder={t("Address")}
                      value={formik?.values?.address?.address}
                      onChange={(event: any) =>
                        formik.setFieldValue("address.address", event?.target?.value)
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-primary whitespace-nowrap"
                      onClick={handleOpenSearchAddress}
                    >
                      {t("Find Address")}
                    </button>
                  </div>

                  {formik.touched.address?.address && formik.errors.address?.address ? (
                    <div className="text-danger mt-2">
                      {formik.errors.address.address}
                    </div>
                  ) : null}
                </div>
              </Col>
              {
                !!formik?.values?.address?.address && (
                  <>
                    <Col lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="address" className="form-label">
                          {t("Short Address")}
                        </Label>
                        <Input
                          name="short_address"
                          type="text"
                          className="form-control"
                          id="short_address"
                          placeholder={t("Short Address")}
                          value={formik?.values?.address?.short_address}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "address.short_address",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.address?.short_address &&
                        formik.errors.address?.short_address ? (
                          <div className="text-danger mt-2">
                            {formik.errors.address?.short_address}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col md={6} lg={3}>
                      <div className="mb-3">
                        <Label htmlFor="address" className="form-label">
                          {t("Latitude")}
                        </Label>
                        <Input
                          name="lat"
                          type="text"
                          className="form-control"
                          id="lat"
                          placeholder={t("Latitude")}
                          value={formik?.values?.address?.lat}
                          onChange={(event: any) =>
                            formik.setFieldValue("address.lat", Number.parseFloat(event?.target?.value || 0))
                          }
                        />
                      </div>
                    </Col>

                    <Col md={6} lg={3}>
                      <div className="mb-3">
                        <Label htmlFor="address" className="form-label">
                          {t("Longitude")}
                        </Label>
                        <Input
                          name="long"
                          type="text"
                          className="form-control"
                          id="long"
                          placeholder={t("Longitude")}
                          value={formik?.values?.address?.long}
                          onChange={(event: any) =>
                            formik.setFieldValue("address.long", Number.parseFloat(event?.target?.value || 0))
                          }
                        />
                      </div>
                    </Col>
                  </>
                )
              }
              
              <Col lg={3}>
                <div className="mb-3">
                  <Label htmlFor="postal_code" className="form-label">
                    {t("Postal Code")}
                  </Label>
                  <Input
                    name="postal_code"
                    type="text"
                    className="form-control"
                    id="postal_code"
                    placeholder={t("Postal Code")}
                    value={formik?.values?.address?.postal_code}
                    onChange={(event: any) =>
                      formik.setFieldValue("address.postal_code", event?.target?.value)
                    }
                  />
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Label htmlFor="region1" className="form-label">
                    {t("Province/Metropolitan City")}
                  </Label>
                  <Input
                    name="region1"
                    type="text"
                    className="form-control"
                    id="region1"
                    placeholder={t("Province/Metropolitan City")}
                    value={formik?.values?.address?.region1}
                    onChange={(event: any) =>
                      formik.setFieldValue("address.region1", event?.target?.value)
                    }
                  />
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Label htmlFor="region2" className="form-label">
                    {t("City/District")}
                  </Label>
                  <Input
                    name="region2"
                    type="text"
                    className="form-control"
                    id="region2"
                    placeholder={t("City/District")}
                    value={formik?.values?.address?.region2}
                    onChange={(event: any) =>
                      formik.setFieldValue("address.region2", event?.target?.value)
                    }
                  />
                </div>
              </Col>

              <Col lg={3}>
                <div className="mb-3">
                  <Label htmlFor="region3" className="form-label">
                    {t("Town/Neighborhood")}
                  </Label>
                  <Input
                    name="region3"
                    type="text"
                    className="form-control"
                    id="region3"
                    placeholder={t("Town/Neighborhood")}
                    value={formik?.values?.address?.region3}
                    onChange={(event: any) =>
                      formik.setFieldValue("address.region3", event?.target?.value)
                    }
                  />
                </div>
              </Col>
              <Col lg={12}>
                {!!isLoadingMap && (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 48 }} spin />
                      }
                    />
                  </div>
                )}
                
                {(formik?.values?.address?.lat && formik?.values?.address?.long && !isLoadingMap) ? (
                    <div className="mb-3">
                      <NaverMap
                        nameAddress={formik?.values?.address?.address}
                        latDefault={formik?.values?.address?.lat}
                        lngDefault={formik?.values?.address?.long}
                      />
                    </div>
                  ) : ''}
              </Col>
            </Row>
          </div>

          <div
            className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
            style={{
              border: "1px dashed var(--vz-primary)",
              borderRadius: "4px",
            }}
          >
            <span
              className="text-primary position-absolute fs-13 px-1"
              style={{
                backgroundColor: "var(--vz-secondary-bg)",
                top: "0px",
                transform: "translateY(-50%)",
              }}
            >
              {t("Contact")}
            </span>
            <Row>
              <Col md={6} lg={4}>
                <div className="mb-3">
                  <Label htmlFor="contact_name" className="form-label">
                    {t("Contact Name")}
                  </Label>
                  <Input
                    name="contact_name"
                    type="text"
                    className="form-control"
                    id="contact_name"
                    placeholder={t("Contact Name")}
                    value={formik?.values?.contact_name}
                    onChange={(event: any) =>
                      formik.setFieldValue("contact_name", event?.target?.value)
                    }
                  />
                  {formik.touched.contact_name && formik.errors.contact_name ? (
                    <div className="text-danger mt-2">
                      {formik.errors.contact_name}
                    </div>
                  ) : null}
                </div>
              </Col>
              <Col md={6} lg={4}>
                <div className="mb-3">
                  <Label htmlFor="contact_phone" className="form-label">
                    {t("Contact Phone")} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="contact_phone"
                    type="text"
                    className="form-control"
                    id="contact_phone"
                    placeholder={t("Contact Phone")}
                    value={formik?.values?.contact_phone}
                    onChange={(event: any) =>
                      formik.setFieldValue(
                        "contact_phone",
                        event?.target?.value
                      )
                    }
                  />
                  {formik.touched.contact_phone &&
                  formik.errors.contact_phone ? (
                    <div className="text-danger mt-2">
                      {formik.errors.contact_phone}
                    </div>
                  ) : null}
                </div>
              </Col>
              <Col md={12} lg={4}>
                <div className="mb-3">
                  <Label htmlFor="contact_email" className="form-label">
                    {t("Contact Email")} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="contact_email"
                    type="text"
                    className="form-control"
                    id="contact_email"
                    placeholder={t("Contact Email")}
                    value={formik?.values?.contact_email}
                    onChange={(event: any) =>
                      formik.setFieldValue(
                        "contact_email",
                        event?.target?.value
                      )
                    }
                  />
                  {formik.touched.contact_email &&
                  formik.errors.contact_email ? (
                    <div className="text-danger mt-2">
                      {formik.errors.contact_email}
                    </div>
                  ) : null}
                </div>
              </Col>
            </Row>
          </div>

          <div
            className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
            style={{
              border: "1px dashed var(--vz-primary)",
              borderRadius: "4px",
            }}
          >
            <span
              className="text-primary position-absolute fs-13 px-1"
              style={{
                backgroundColor: "var(--vz-secondary-bg)",
                top: "0px",
                transform: "translateY(-50%)",
              }}
            >
              {t("Account")}
            </span>

            <Row>
              <Col md={6} lg={4}>
                <div className="mb-3">
                  <Label htmlFor="email" className="form-label">
                    {t("Email")}
                  </Label>
                  <Input
                    name="business_account.email"
                    type="text"
                    className="form-control"
                    id="email"
                    placeholder={t("Email")}
                    value={formik.values?.business_account?.email || ""}
                    onChange={formik.handleChange}
                    disabled={!!id}
                  />
                  {formik.touched?.business_account?.email &&
                  formik.errors?.business_account?.email ? (
                    <div className="text-danger mt-2">
                      {formik.errors.business_account.email}
                    </div>
                  ) : null}
                </div>
              </Col>
              {
                !id && (
                  <Col md={6} lg={4}>
                    <div className="mb-3">
                      <Label htmlFor="contact_phone" className="form-label">
                        {t("Password")}
                      </Label>
                      <Input
                        name="business_account.password"
                        type="password"
                        className="form-control"
                        id="password"
                        placeholder={t("Password")}
                        value={formik?.values?.business_account?.password}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "business_account.password",
                            event?.target?.value
                          )
                        }
                        disabled={!!id}
                      />
                      {formik.touched?.business_account?.password &&
                      formik.errors?.business_account?.password ? (
                        <div className="text-danger mt-2">
                          {formik.errors?.business_account?.password}
                        </div>
                      ) : null}
                    </div>
                  </Col>        
                )
              }
            </Row>
          </div>
          
          <Col lg={12}>
            <div className="mb-3 d-flex gap-2">
              <Label htmlFor="region3" className="form-label">
                {t("Status")}
              </Label>
              <Switch
                id="company-status"
                value={formik?.values?.is_active}
                onChange={(value: any) => {
                  formik.setFieldValue("is_active", value)
                }}
              />
            </div>
          </Col>

          <Row>
            <Col lg={12}>
              <div className="hstack gap-2 justify-content-end">
                {isHavePermissionRole(
                  ROLES_FOR_APP.USER_UPDATE,
                  userPermissions
                ) && (
                  <button type="submit" className="btn btn-primary fs-14">
                    {isLoading ? (
                      <Spinner size="sm" className="me-2"></Spinner>
                    ) : !!id ? (
                      <i className="ri-login-circle-line align-bottom me-2 "></i>
                    ) : (
                      <i className="ri-add-fill align-bottom me-2"></i>
                    )}
                    {id
                      ? t("Button Update")
                      : t("Button Create")}
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </form>
      </div>
      {isOpenSearchAddress && (
        <Modal
          isOpen={!!isOpenSearchAddress}
          centered={true}
          size="lg"
          scrollable={false}
        >
          <ModalHeader toggle={handleCloseModalAddress}>
            {"Search Address"}
          </ModalHeader>
          <ModalBody>
            <SearchAddress onSetValueAddress={onSetValueAddress} />
          </ModalBody>
        </Modal>
      )}
      <ToastContainer closeButton={false} limit={1} />
    </React.Fragment>
  );
};

export default CompanyForm;
